import React, { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as DayPicker } from './ui/calendar';

/**
 * Lightweight date picker that swaps the native <input type="date"> control
 * for a button-triggered shadcn popover with a mini month calendar.
 *
 * Props:
 *  - value: string in "YYYY-MM-DD" format (or empty string)
 *  - onChange: (newValue: "YYYY-MM-DD") => void
 *  - placeholder: string shown when value is empty
 *  - testId: data-testid
 */
const DatePickerPopover = ({ value, onChange, placeholder = 'Pick a date', testId }) => {
  const [open, setOpen] = useState(false);

  const selectedDate = value ? parseISO(value) : undefined;
  const display = selectedDate ? format(selectedDate, 'EEE, MMM d, yyyy') : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-full flex items-center gap-2 px-4 py-3 bg-black border border-gray-600 rounded-lg text-left text-white hover:border-white focus:border-white focus:outline-none transition-colors"
        >
          <CalendarDays size={16} className="text-gray-400" />
          <span className={selectedDate ? 'text-white' : 'text-gray-500'}>{display}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-black border border-white/20 text-white"
        align="start"
      >
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={(d) => {
            if (!d) return;
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            onChange(`${y}-${m}-${day}`);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerPopover;
